<template>
  <div v-show="!disabled" class="editor-guides" :class="{ 'is-dragging': !!drag }" :style="drag ? { cursor: drag.axis === 'horizontal' ? 'ns-resize' : 'ew-resize' } : {}" data-editor-guides @contextmenu.prevent.stop>
    <div class="editor-ruler-corner" aria-hidden="true"><i class="el-icon-view"></i></div>
    <svg ref="horizontal" class="editor-ruler editor-ruler--horizontal" :width="size.width" height="22" tabindex="0" role="button" focusable="true"
         aria-label="水平标尺，向下拖出水平辅助线" aria-keyshortcuts="Enter Space" @pointerdown="beginDrag('horizontal', $event)" @keydown.stop="rulerKey('horizontal', $event)">
      <title>向下拖出水平辅助线；也可按 Enter 或空格添加</title>
      <g v-for="tick in horizontalTicks" :key="tick.value" :transform="'translate(' + tick.position + ',0)'">
        <path :d="tick.major ? 'M0 15 V22' : 'M0 19 V22'" /><text v-if="tick.major" x="3" y="11">{{ tick.value }}</text>
      </g>
    </svg>
    <svg ref="vertical" class="editor-ruler editor-ruler--vertical" width="22" :height="size.height" tabindex="0" role="button" focusable="true"
         aria-label="垂直标尺，向右拖出垂直辅助线" aria-keyshortcuts="Enter Space" @pointerdown="beginDrag('vertical', $event)" @keydown.stop="rulerKey('vertical', $event)">
      <title>向右拖出垂直辅助线；也可按 Enter 或空格添加</title>
      <g v-for="tick in verticalTicks" :key="tick.value" :transform="'translate(0,' + tick.position + ')'">
        <path :d="tick.major ? 'M15 0 H22' : 'M19 0 H22'" /><text v-if="tick.major" transform="rotate(-90)" x="3" y="11">{{ tick.value }}</text>
      </g>
    </svg>
    <div class="editor-guide-layer">
      <button v-for="guide in visibleGuides" :key="guide.id" type="button" class="editor-guide" :class="['editor-guide--' + guide.axis, { 'is-dragging': drag && drag.id === guide.id }]"
              :data-guide-id="guide.id" :style="lineStyle(guide)" role="separator" :aria-orientation="guide.axis" :aria-valuenow="guide.value" :aria-valuemin="0" :aria-valuemax="limit(guide.axis)"
              :aria-label="description(guide)" :title="description(guide) + '；拖动调整，拖回标尺或双击删除；方向键微调，Shift 加速，Delete 删除'"
              @pointerdown="beginDrag(guide.axis, $event, guide)" @keydown.stop="guideKey(guide, $event)" @dblclick.prevent.stop="removeGuide(guide)">
        <span class="editor-guide__value" aria-hidden="true">{{ guide.axis === 'horizontal' ? 'Y' : 'X' }}: {{ guide.value }} px</span>
      </button>
    </div>
  </div>
</template>

<script>
import { rulerTicks } from '@/editor/core/viewport'
import { RULER_SIZE, guideLimit, guidePosition, guideFromPointer, guideAtCenter } from '@/editor/core/guides'

export default {
  props: {
    viewport: { type: Object, required: true }, size: { type: Object, required: true }, config: { type: Object, required: true },
    scope: { type: String, required: true }, disabled: Boolean
  },
  data: () => ({ pages: {}, drag: null, sequence: 0 }),
  computed: {
    horizontalTicks () { return rulerTicks(this.size.width, this.viewport.panX, this.viewport.zoom) },
    verticalTicks () { return rulerTicks(this.size.height, this.viewport.panY, this.viewport.zoom) },
    currentGuides () { return this.pages[this.scope] || [] },
    visibleGuides () {
      const rows = this.currentGuides.filter(guide => !this.drag || guide.id !== this.drag.id)
      if (this.drag && this.drag.inside) rows.push(this.drag)
      return rows.filter(guide => guide.value >= 0 && guide.value <= this.limit(guide.axis))
    }
  },
  watch: {
    scope () { this.cancelDrag(false) },
    disabled (value) { if (value) this.cancelDrag(false) }
  },
  beforeDestroy () { this.cancelDrag(false) },
  methods: {
    limit (axis) { return guideLimit(axis, this.config) },
    description (guide) { return (guide.axis === 'horizontal' ? '水平辅助线 Y：' : '垂直辅助线 X：') + guide.value },
    lineStyle (guide) { return { [guide.axis === 'horizontal' ? 'top' : 'left']: guidePosition(guide, this.viewport) + 'px' } },
    focusControl (id, axis) {
      this.$nextTick(() => {
        if (this._isDestroyed) return
        const element = id && this.$el.querySelector('[data-guide-id="' + id + '"]') || this.$refs[axis]
        if (element) element.focus({ preventScroll: true })
      })
    },
    beginDrag (axis, event, guide) {
      if (this.disabled || event.button !== 0 || event.isPrimary === false) return
      event.preventDefault(); event.stopPropagation()
      this.cancelDrag(false); this.$emit('begin')
      const rect = this.$el.getBoundingClientRect()
      const offset = guide ? (axis === 'horizontal' ? event.clientY - rect.top : event.clientX - rect.left) - RULER_SIZE - guidePosition(guide, this.viewport) : 0
      this.drag = { id: guide ? guide.id : 'guide-' + ++this.sequence, axis, offset, pointerId: event.pointerId,
        ...guideFromPointer(axis, { x: event.clientX, y: event.clientY }, rect, this.viewport, this.config, offset) }
      event.currentTarget.focus({ preventScroll: true })
      window.addEventListener('pointermove', this.moveGuide)
      window.addEventListener('pointerup', this.finishGuide)
      window.addEventListener('pointercancel', this.cancelPointer)
      window.addEventListener('blur', this.cancelPointer)
      window.addEventListener('keydown', this.dragKey, true)
    },
    moveGuide (event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return
      event.preventDefault()
      Object.assign(this.drag, guideFromPointer(this.drag.axis, { x: event.clientX, y: event.clientY }, this.$el.getBoundingClientRect(), this.viewport, this.config, this.drag.offset))
    },
    finishGuide (event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return
      this.moveGuide(event)
      const draft = this.drag, rows = this.currentGuides.filter(guide => guide.id !== draft.id)
      if (draft.inside) rows.push({ id: draft.id, axis: draft.axis, value: draft.value })
      this.$set(this.pages, this.scope, rows)
      this.cancelDrag(false); this.focusControl(draft.inside ? draft.id : null, draft.axis)
    },
    cancelPointer () { this.cancelDrag(false) },
    cancelDrag (focus = true) {
      const draft = this.drag
      this.drag = null
      window.removeEventListener('pointermove', this.moveGuide)
      window.removeEventListener('pointerup', this.finishGuide)
      window.removeEventListener('pointercancel', this.cancelPointer)
      window.removeEventListener('blur', this.cancelPointer)
      window.removeEventListener('keydown', this.dragKey, true)
      if (draft && focus) this.focusControl(this.currentGuides.some(guide => guide.id === draft.id) ? draft.id : null, draft.axis)
    },
    dragKey (event) {
      if (event.key !== 'Escape') return
      event.preventDefault(); event.stopPropagation(); this.cancelDrag()
    },
    rulerKey (axis, event) {
      if (this.disabled || !['Enter', ' '].includes(event.key)) return
      event.preventDefault()
      if (event.repeat) return
      const guide = { id: 'guide-' + ++this.sequence, axis, value: guideAtCenter(axis, this.size, this.viewport, this.config) }
      this.$set(this.pages, this.scope, [...this.currentGuides, guide]); this.focusControl(guide.id, axis)
    },
    removeGuide (guide) { this.$set(this.pages, this.scope, this.currentGuides.filter(row => row.id !== guide.id)); this.focusControl(null, guide.axis) },
    guideKey (guide, event) {
      if (this.disabled || event.ctrlKey || event.metaKey || event.altKey) return
      if (['Delete', 'Backspace'].includes(event.key)) { event.preventDefault(); this.removeGuide(guide); return }
      const keys = guide.axis === 'horizontal' ? ['ArrowUp', 'ArrowDown'] : ['ArrowLeft', 'ArrowRight']
      if (!keys.includes(event.key) && !['Home', 'End'].includes(event.key)) return
      event.preventDefault()
      const value = event.key === 'Home' ? 0 : event.key === 'End' ? this.limit(guide.axis) : guide.value + (event.key === keys[0] ? -1 : 1) * (event.shiftKey ? 10 : 1)
      guide.value = Math.max(0, Math.min(this.limit(guide.axis), value))
    }
  }
}
</script>
